import { Link } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { SITE_URL } from "@/lib/seo";
import { BacklinkEmbed } from "@/components/BacklinkEmbed";

const DESCRIPTION =
  "Free live gold price API. Real-time 24K, 22K & 18K spot bullion rates in USD, PKR and INR as a clean JSON REST feed. Integrate in JavaScript, Python or PHP in under 3 minutes.";

const ENDPOINT = `${SITE_URL}/api/public/hooks/gold-rates`;

const SAMPLE = `{
  "spotUsdPerOz": 4331.42,
  "pkrPerUsd": 278.11,
  "inrPerUsd": 83.52,
  "perGram24kPkr": 38712,
  "perTola24kPkr": 451487,
  "perGram24kInr": 11623,
  "per10gram24kInr": 116230,
  "fetchedAt": "2026-06-13T08:00:00.000Z"
}`;

const JS = `// JavaScript / Node — fetch the live gold rate feed
const res = await fetch("${ENDPOINT}");
const data = await res.json();
console.log("Spot USD/oz:", data.spotUsdPerOz);
console.log("24K per tola PKR:", data.perTola24kPkr);`;

const PY = `# Python — fetch live gold rate
import requests

data = requests.get("${ENDPOINT}").json()
print("Spot USD/oz:", data["spotUsdPerOz"])
print("24K per 10g INR:", data["per10gram24kInr"])`;

const PHP = `<?php
// PHP — fetch live gold rate via cURL
$ch = curl_init("${ENDPOINT}");
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
$data = json_decode(curl_exec($ch), true);
curl_close($ch);
echo "Spot USD/oz: " . $data["spotUsdPerOz"];`;

export function ApiPage() {
  return (
    <Layout>
      <section className="mx-auto max-w-5xl px-4 pt-10 pb-6">
        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/" className="hover:text-gold">Home</Link> / Developer / Gold Rate API
        </nav>
        <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-gold">For Developers</span>
        <h1 className="mt-3 text-3xl md:text-5xl font-black tracking-tight">Free Live Gold Price API</h1>
        <p className="mt-3 text-muted-foreground max-w-3xl">{DESCRIPTION}</p>
      </section>

      <section className="mx-auto max-w-5xl px-4 pb-8 grid sm:grid-cols-3 gap-3">
        {[
          { k: "Latency", v: "< 80 ms global edge" },
          { k: "Uptime", v: "99.95% rolling 30 days" },
          { k: "Price", v: "Free · 60 req/min" },
        ].map((c) => (
          <div key={c.k} className="glass rounded-2xl p-4">
            <div className="text-[11px] uppercase text-muted-foreground tracking-wider">{c.k}</div>
            <div className="font-bold text-gold mt-1">{c.v}</div>
          </div>
        ))}
      </section>

      <section className="mx-auto max-w-5xl px-4 pb-8">
        <div className="glass rounded-3xl p-5 md:p-7">
          <h2 className="text-xl font-bold">Endpoint</h2>
          <pre className="mt-3 overflow-x-auto rounded-xl border border-border bg-card/60 p-4 text-xs"><code className="font-mono">GET {ENDPOINT}</code></pre>
          <h3 className="mt-6 font-bold">Sample JSON response</h3>
          <pre className="mt-2 overflow-x-auto rounded-xl border border-border bg-card/60 p-4 text-xs"><code className="font-mono whitespace-pre">{SAMPLE}</code></pre>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 pb-8 grid gap-4 md:grid-cols-3">
        {[
          { lang: "JavaScript", code: JS },
          { lang: "Python", code: PY },
          { lang: "PHP", code: PHP },
        ].map((b) => (
          <div key={b.lang} className="glass rounded-2xl p-4">
            <h3 className="font-bold mb-2">{b.lang}</h3>
            <pre className="overflow-x-auto rounded-lg border border-border bg-card/60 p-3 text-[11px] leading-relaxed"><code className="font-mono whitespace-pre">{b.code}</code></pre>
          </div>
        ))}
      </section>

      <section className="mx-auto max-w-5xl px-4 pb-12">
        <div className="glass rounded-3xl p-5 md:p-7">
          <h2 className="text-xl font-bold">Rate limits</h2>
          <table className="mt-3 w-full text-sm">
            <thead className="text-left text-xs uppercase text-muted-foreground"><tr><th className="py-2">Tier</th><th>Requests / min</th><th>Cache TTL</th></tr></thead>
            <tbody>
              <tr className="border-t border-border"><td className="py-2 font-semibold">Free (default)</td><td>60</td><td>60 s</td></tr>
              <tr className="border-t border-border"><td className="py-2 font-semibold">High volume</td><td>600</td><td>30 s</td></tr>
            </tbody>
          </table>
          <p className="mt-4 text-xs text-muted-foreground">High-volume access is granted on request — <Link to="/contact" className="text-gold hover:underline">contact us</Link> with your domain and use case.</p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 pb-16">
        <div className="rounded-3xl border-2 border-amber-500/40 bg-gradient-to-br from-amber-500/10 via-card/40 to-background p-6 md:p-8">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-background">Required</span>
            <h2 className="text-xl md:text-2xl font-bold">Terms of Free Usage</h2>
          </div>
          <p className="mt-3 text-sm text-muted-foreground max-w-3xl">
            The free programmatic REST JSON data tier is provided at no cost in exchange for active
            domain attribution validation. To remain compliant with the free tier, any website or
            application that publicly displays values returned by this API must include a visible
            credit linking back to <strong className="text-foreground">drhint.com</strong>.
            Attribution-removed deployments are auto-detected and rate-limited.
          </p>
          <ul className="mt-4 grid gap-2 text-sm md:grid-cols-3">
            <li className="rounded-xl border border-border bg-card/60 p-3"><strong>✅ Allowed</strong><br /><span className="text-muted-foreground">Personal sites, blogs, commercial apps, dashboards.</span></li>
            <li className="rounded-xl border border-border bg-card/60 p-3"><strong>✅ Required</strong><br /><span className="text-muted-foreground">Visible credit link on every page that displays our data.</span></li>
            <li className="rounded-xl border border-border bg-card/60 p-3"><strong>❌ Not allowed</strong><br /><span className="text-muted-foreground">Reselling the raw feed or hiding the source credit.</span></li>
          </ul>

          <BacklinkEmbed
            variant="api"
            title="📎 Ready-to-Copy Footer Credit Snippet"
            ctaLabel="Get attribution code"
            targetUrl={SITE_URL}
            defaultOpen
          />
        </div>
      </section>
    </Layout>
  );
}